// src/index.ts
var rateCardsMissingReporter;
var reportedRateCardErrors = /* @__PURE__ */ new Set();
var setRateCardsMissingReporter = (reporter) => {
  rateCardsMissingReporter = reporter;
};
var reportRateCardError = (error, merchantId) => {
  if (!rateCardsMissingReporter || reportedRateCardErrors.has(error.message)) {
    return;
  }
  reportedRateCardErrors.add(error.message);
  rateCardsMissingReporter(error, { merchantId });
};
var merchantIdFromCards = (rateCards) => {
  var _a, _b;
  return (_b = (_a = rateCards.find((card) => card.merchantID != null)) == null ? void 0 : _a.merchantID) != null ? _b : void 0;
};
var getMerchantRateCards = (merchant) => {
  const rateCards = merchant == null ? void 0 : merchant.merchantRateCards;
  if (!(rateCards == null ? void 0 : rateCards.length)) {
    const merchantId = merchant == null ? void 0 : merchant.id;
    const error = new Error(
      `No rate cards on merchant payload (id: ${merchantId != null ? merchantId : "unknown"}). The merchantRateCards subresource must be fetched and the merchant migrated.`
    );
    reportRateCardError(error, merchantId);
    throw error;
  }
  return rateCards;
};
var fromBps = (bps) => bps / 1e4;
var PRODUCT_ALIASES = {
  prime: "default"
};
var canonicalProduct = (product) => {
  var _a;
  return (_a = PRODUCT_ALIASES[product]) != null ? _a : product;
};
var rateCardTermToPaymentOption = (term) => {
  var _a;
  return {
    numberOfMonths: term.termMonths,
    interestRate: fromBps(term.aprBps),
    rebateDays: term.rebateDays,
    netFee: fromBps(term.subsidyFeeBps),
    vat: fromBps((_a = term.vatBps) != null ? _a : 0),
    isRegulated: term.isRegulated !== false,
    isAvailableForUnregulatedMerchants: term.availableForUnregulatedMerchants
  };
};
var getPaymentOptions = (rateCards, riskProfileName) => {
  var _a;
  const rateCard = rateCards.find(
    (card) => canonicalProduct(card.product) === riskProfileName
  );
  if (!rateCard) {
    const error = new Error(
      `No rate card for risk profile "${riskProfileName}" (merchant has cards for: ${rateCards.map((card) => card.product).join(", ") || "none"}). Rate card configuration is required \u2014 no fallback.`
    );
    reportRateCardError(error, merchantIdFromCards(rateCards));
    throw error;
  }
  return ((_a = rateCard.terms) != null ? _a : []).map(rateCardTermToPaymentOption);
};
var findMerchantRateCard = (rateCards, riskProfileName) => rateCards.find((card) => canonicalProduct(card.product) === riskProfileName);
var RATE_CARD_TIERS = [
  "standard",
  "key_accounts",
  "enterprise"
];
var getRateCardTierRank = (type) => {
  const index = RATE_CARD_TIERS.indexOf(type);
  return index === -1 ? RATE_CARD_TIERS.length : index;
};
var hasRateCard = (rateCards, riskProfileName) => rateCards.some((card) => canonicalProduct(card.product) === riskProfileName);
var getCurrentRateCardVersion = (card) => {
  var _a;
  const now = Date.now();
  const effective = ((_a = card.versions) != null ? _a : []).filter(
    (version) => version.publishedAt && Date.parse(version.effectiveFrom) <= now
  );
  if (!effective.length) {
    return void 0;
  }
  return effective.reduce((latest, version) => {
    const byDate = Date.parse(version.effectiveFrom) - Date.parse(latest.effectiveFrom);
    if (byDate > 0)
      return version;
    if (byDate < 0)
      return latest;
    return version.versionNumber > latest.versionNumber ? version : latest;
  });
};
var getNextScheduledRateCardVersion = (card) => {
  var _a;
  const now = Date.now();
  const scheduled = ((_a = card.versions) != null ? _a : []).filter(
    (version) => version.publishedAt && Date.parse(version.effectiveFrom) > now
  );
  if (!scheduled.length) {
    return void 0;
  }
  return scheduled.reduce((earliest, version) => {
    const byDate = Date.parse(version.effectiveFrom) - Date.parse(earliest.effectiveFrom);
    if (byDate < 0)
      return version;
    if (byDate > 0)
      return earliest;
    return version.versionNumber < earliest.versionNumber ? version : earliest;
  });
};
var getRateCardsForProfile = (rateCards, riskProfileName) => rateCards.filter((card) => canonicalProduct(card.pricingProduct) === riskProfileName).sort((a, b) => getRateCardTierRank(a.type) - getRateCardTierRank(b.type));
var findStandardRateCard = (rateCards, riskProfileName) => rateCards.find(
  (card) => card.type === "standard" && canonicalProduct(card.pricingProduct) === riskProfileName
);
var getRateCardOptions = (card) => {
  const version = getCurrentRateCardVersion(card);
  if (!version) {
    return [];
  }
  return version.terms.filter((term) => term.enable).map(rateCardTermToPaymentOption);
};
var isRiskProfileAvailableForNewApplications = (riskProfile) => riskProfile.validForNewOffer !== false;
var isLoanOptionAvailableForUnregulatedMerchants = (rateCards, riskProfileName, option) => {
  var _a;
  const profileOptions = getPaymentOptions(rateCards, riskProfileName);
  return !!((_a = profileOptions.find(
    (o) => o.numberOfMonths === option.numberOfMonths && o.interestRate === option.interestRate
  )) == null ? void 0 : _a.isAvailableForUnregulatedMerchants);
};
var getConsumerFacingTermOptions = (loanOptions, maxTerm) => {
  const availableOptions = loanOptions.filter((option) => option.numberOfMonths <= maxTerm).sort((a, b) => a.numberOfMonths - b.numberOfMonths);
  const lastThreeOptions = availableOptions.slice(-3);
  const interestFree12MonthsOption = availableOptions.find(
    (option) => option.numberOfMonths === 12 && option.interestRate === 0
  );
  if (maxTerm > 12 && !lastThreeOptions.some((option) => option.numberOfMonths === 12) && interestFree12MonthsOption) {
    return [interestFree12MonthsOption, ...lastThreeOptions.slice(1)];
  }
  return lastThreeOptions;
};
var feeCalculator = (rateCards, riskProfileName, paymentOption, price = 0) => {
  const { numberOfMonths, interestRate = 0 } = paymentOption;
  const option = getPaymentOptions(rateCards, riskProfileName).find(
    (o) => o.numberOfMonths === numberOfMonths && o.interestRate === interestRate
  );
  if (!option) {
    const error = new Error(
      `No rate card term for "${riskProfileName}" at ${numberOfMonths} months / ${interestRate * 100}% APR \u2014 the payment option is not in the merchant's rate card.`
    );
    reportRateCardError(error, merchantIdFromCards(rateCards));
    throw error;
  }
  const fee = option.netFee * (1 + option.vat);
  return {
    ...option,
    fee,
    netFeeAmount: +(option.netFee * price).toFixed(2),
    feeAmount: +(fee * price).toFixed(2)
  };
};
export {
  RATE_CARD_TIERS,
  feeCalculator,
  findMerchantRateCard,
  findStandardRateCard,
  getConsumerFacingTermOptions,
  getCurrentRateCardVersion,
  getMerchantRateCards,
  getNextScheduledRateCardVersion,
  getPaymentOptions,
  getRateCardOptions,
  getRateCardTierRank,
  getRateCardsForProfile,
  hasRateCard,
  isLoanOptionAvailableForUnregulatedMerchants,
  isRiskProfileAvailableForNewApplications,
  setRateCardsMissingReporter
};
